import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Activity,
  Wifi,
  WifiOff,
  Trophy,
  ArrowLeft,
  User,
  Target,
  TrendingUp,
  MessageSquareText,
  BarChart3,
  Sparkles,
  Radio,
} from 'lucide-react';
import { API, getWebSocketBaseUrl, shouldUseWebSockets } from '../lib/api';
import { BrandLogo } from '../components/BrandLogo';
import { ContextualState } from '../components/StatePanel';

const getBallDisplay = (ball) => {
  if (ball.is_wicket) return 'W';
  if (ball.is_wide) return ball.runs > 0 ? `Wd${ball.runs}` : 'Wd';
  if (ball.is_no_ball) return ball.runs > 0 ? `Nb${ball.runs}` : 'Nb';
  return `${ball.runs}`;
};

const getBallStyle = (ball) => {
  if (ball.is_wicket) return 'bg-red-500/15 text-red-200 border-red-500/30';
  if (ball.is_wide) return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (ball.is_no_ball) return 'bg-orange-500/15 text-orange-200 border-orange-500/30';
  if (ball.runs >= 4) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30';
  if (ball.runs === 0) return 'bg-white/5 text-white/55 border-white/10';
  return 'bg-primary/10 text-primary border-primary/20';
};

const getOverBreakdown = (innings) => {
  if (!innings?.ball_by_ball) return [];

  const overs = [];
  let currentOver = { number: 0, balls: [], runs: 0, wickets: 0 };
  let ballCount = 0;

  innings.ball_by_ball.forEach((ball) => {
    const isLegal = !ball.is_wide && !ball.is_no_ball;
    let runsInBall = ball.runs;
    if (ball.is_wide || ball.is_no_ball) runsInBall += 1;

    currentOver.balls.push(ball);
    currentOver.runs += runsInBall;
    if (ball.is_wicket) currentOver.wickets += 1;

    if (isLegal) {
      ballCount += 1;
      if (ballCount === 6) {
        overs.push({ ...currentOver });
        currentOver = { number: overs.length, balls: [], runs: 0, wickets: 0 };
        ballCount = 0;
      }
    }
  });

  if (currentOver.balls.length > 0) overs.push({ ...currentOver });
  return overs;
};

const calculatePlayerStats = (innings) => {
  if (!innings?.ball_by_ball) return { batsmen: {}, bowlers: {} };

  const batsmen = {};
  const bowlers = {};

  innings.ball_by_ball.forEach((ball) => {
    if (ball.batsman_id) {
      if (!batsmen[ball.batsman_id]) {
        batsmen[ball.batsman_id] = { runs: 0, balls: 0, fours: 0, sixes: 0, isOut: false };
      }
      if (!ball.is_wide) batsmen[ball.batsman_id].balls += 1;
      if (!ball.is_bye && !ball.is_leg_bye && !ball.is_wide) {
        batsmen[ball.batsman_id].runs += ball.runs;
        if (ball.runs === 4) batsmen[ball.batsman_id].fours += 1;
        if (ball.runs === 6) batsmen[ball.batsman_id].sixes += 1;
      }
      if (ball.is_wicket) {
        batsmen[ball.batsman_id].isOut = true;
        batsmen[ball.batsman_id].wicketType = ball.wicket_type;
      }
    }

    if (ball.bowler_id) {
      if (!bowlers[ball.bowler_id]) {
        bowlers[ball.bowler_id] = { overs: 0, balls: 0, runs: 0, wickets: 0, wides: 0, noBalls: 0 };
      }
      const isLegal = !ball.is_wide && !ball.is_no_ball;
      if (isLegal) {
        bowlers[ball.bowler_id].balls += 1;
        if (bowlers[ball.bowler_id].balls === 6) {
          bowlers[ball.bowler_id].overs += 1;
          bowlers[ball.bowler_id].balls = 0;
        }
      }

      let runsGiven = ball.runs;
      if (ball.is_wide) {
        bowlers[ball.bowler_id].wides += 1;
        runsGiven += 1;
      }
      if (ball.is_no_ball) {
        bowlers[ball.bowler_id].noBalls += 1;
        runsGiven += 1;
      }
      if (!ball.is_bye && !ball.is_leg_bye) bowlers[ball.bowler_id].runs += runsGiven;
      if (ball.is_wicket) bowlers[ball.bowler_id].wickets += 1;
    }
  });

  return { batsmen, bowlers };
};

const getExtrasTotal = (innings) =>
  innings
    ? innings.extras.wides + innings.extras.no_balls + innings.extras.byes + innings.extras.leg_byes
    : 0;

const calculateRunRate = (innings) => {
  if (!innings) return '0.00';
  const oversBowled = innings.overs + innings.balls / 6;
  return oversBowled > 0 ? (innings.runs / oversBowled).toFixed(2) : '0.00';
};

const buildCommentary = (match, getPlayerName) => {
  const items = [];

  (match.innings || []).forEach((innings, inningsIdx) => {
    (innings.ball_by_ball || []).forEach((ball) => {
      const overNumber = `${ball.over}.${ball.ball}`;
      const batsman = getPlayerName(ball.batsman_id);
      const bowler = getPlayerName(ball.bowler_id);

      let title = `${overNumber} ${innings.batting_team?.name}`;
      let detail = `${batsman} faced ${bowler}.`;
      let tone = 'neutral';

      if (ball.is_wicket) {
        title = `${overNumber} WICKET`;
        detail = `${batsman} is out ${ball.wicket_type ? `(${ball.wicket_type})` : ''}. ${bowler} strikes for ${innings.bowling_team?.name}.`;
        tone = 'wicket';
      } else if (ball.is_wide) {
        title = `${overNumber} Wide`;
        detail = `${bowler} sprays it wide. ${innings.batting_team?.name} moves to ${ball.runs + 1} extra run(s).`;
        tone = 'extra';
      } else if (ball.is_no_ball) {
        title = `${overNumber} No Ball`;
        detail = `${bowler} oversteps. ${innings.batting_team?.name} collects ${ball.runs + 1}.`;
        tone = 'extra';
      } else if (ball.runs === 6) {
        title = `${overNumber} SIX`;
        detail = `${batsman} launches ${bowler} over the ropes for a maximum.`;
        tone = 'boundary';
      } else if (ball.runs === 4) {
        title = `${overNumber} FOUR`;
        detail = `${batsman} pierces the field and finds the boundary off ${bowler}.`;
        tone = 'boundary';
      } else if (ball.runs === 0) {
        title = `${overNumber} Dot Ball`;
        detail = `${bowler} keeps it tight. ${batsman} cannot score.`;
      } else {
        title = `${overNumber} ${ball.runs} run${ball.runs > 1 ? 's' : ''}`;
        detail = `${batsman} works ${ball.runs} off ${bowler}.`;
      }

      items.push({
        id: ball.id || `${inningsIdx}-${overNumber}-${title}`,
        overLabel: overNumber,
        inningsLabel: `${inningsIdx + 1}${inningsIdx === 0 ? 'st' : 'nd'} innings`,
        title,
        detail,
        tone,
        timestamp: ball.timestamp,
      });
    });
  });

  return items.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
};

const OverByOverSection = ({ innings, inningsIdx, compact = false }) => {
  const overs = innings ? getOverBreakdown(innings) : [];
  if (overs.length === 0) return null;

  return (
    <Card className="rounded-3xl border-white/10 bg-white/[0.04]">
      <CardHeader className={compact ? 'pb-3' : undefined}>
        <CardTitle className="text-lg text-white">
          {innings.batting_team?.name} - {inningsIdx === 0 ? '1st' : '2nd'} Innings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {overs.map((over, idx) => (
            <div key={idx} className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 sm:flex-row sm:items-center">
              <div className="sm:w-20 sm:text-center">
                <span className="text-sm font-semibold text-white">Over {idx + 1}</span>
              </div>
              <div className="flex flex-1 flex-wrap gap-1.5">
                {over.balls.map((ball, ballIdx) => (
                  <div
                    key={ball.id || `${idx}-${ballIdx}`}
                    className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold ${getBallStyle(ball)}`}
                  >
                    {getBallDisplay(ball)}
                  </div>
                ))}
              </div>
              <div className="sm:w-24 sm:text-right">
                <span className="font-bold text-white">{over.runs} runs</span>
                {over.wickets > 0 && <span className="ml-1 text-red-300">({over.wickets}W)</span>}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ComparisonBar = ({ label, leftLabel, rightLabel, leftValue, rightValue, accent = 'primary' }) => {
  const maxValue = Math.max(leftValue, rightValue, 1);
  const leftWidth = (leftValue / maxValue) * 100;
  const rightWidth = (rightValue / maxValue) * 100;
  const tone =
    accent === 'primary'
      ? 'from-primary/90 to-primary/35'
      : accent === 'violet'
      ? 'from-violet-500 to-violet-300'
      : 'from-emerald-500 to-emerald-300';

  return (
    <div className="space-y-2 rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-white/60">{leftLabel}</span>
        <span className="text-white/40">{label}</span>
        <span className="font-medium text-white/60">{rightLabel}</span>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
        <span className="text-lg font-bold text-white">{leftValue}</span>
        <div className="space-y-2">
          <div className="h-2 rounded-full bg-white/5">
            <div className={`h-2 rounded-full bg-gradient-to-r ${tone}`} style={{ width: `${leftWidth}%` }} />
          </div>
          <div className="h-2 rounded-full bg-white/5">
            <div className={`ml-auto h-2 rounded-full bg-gradient-to-l ${tone}`} style={{ width: `${rightWidth}%` }} />
          </div>
        </div>
        <span className="text-lg font-bold text-white">{rightValue}</span>
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(170,255,0,0.08),transparent_20%),linear-gradient(180deg,_rgba(10,12,18,1)_0%,_rgba(12,16,23,1)_100%)] px-4 py-6 text-white">
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="h-14 animate-pulse rounded-2xl border border-white/10 bg-white/[0.05]" />
      <div className="h-64 animate-pulse rounded-[28px] border border-white/10 bg-white/[0.05]" />
      <div className="h-12 animate-pulse rounded-2xl border border-white/10 bg-white/[0.05]" />
      <div className="grid gap-4 md:grid-cols-3">
        <div className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.05]" />
        <div className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.05]" />
        <div className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.05]" />
      </div>
    </div>
  </div>
);

const PublicScoreboard = () => {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState({ team1: null, team2: null });
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const websocketEnabled = shouldUseWebSockets();

  const connectWebSocket = useCallback(() => {
    if (!websocketEnabled) return;

    const wsUrl = getWebSocketBaseUrl();
    if (!wsUrl) return;

    const ws = new WebSocket(`${wsUrl}/ws/match/${matchId}`);
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.match) setMatch(data.match);
    };
    ws.onclose = () => {
      setWsConnected(false);
      setTimeout(connectWebSocket, 3000);
    };
    ws.onerror = () => setWsConnected(false);
    wsRef.current = ws;
  }, [matchId, websocketEnabled]);

  const fetchMatch = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`);
      setMatch(response.data);

      if (response.data.team1?.id && response.data.team2?.id) {
        const [team1Res, team2Res] = await Promise.all([
          axios.get(`${API}/teams/${response.data.team1.id}`),
          axios.get(`${API}/teams/${response.data.team2.id}`),
        ]);
        setTeams({ team1: team1Res.data, team2: team2Res.data });
      }
    } catch (error) {
      console.error('Failed to fetch match');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMatch();
    if (websocketEnabled) connectWebSocket();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchMatch, connectWebSocket, websocketEnabled]);

  useEffect(() => {
    const refreshInterval = setInterval(fetchMatch, websocketEnabled ? 10000 : 5000);
    return () => clearInterval(refreshInterval);
  }, [fetchMatch, websocketEnabled]);

  const getPlayerName = useCallback(
    (playerId) => {
      if (!playerId) return 'Unknown';
      const allPlayers = [...(teams.team1?.players || []), ...(teams.team2?.players || [])];
      return allPlayers.find((player) => player.id === playerId)?.name || 'Unknown';
    },
    [teams]
  );

  const commentaryItems = useMemo(() => (match ? buildCommentary(match, getPlayerName) : []), [match, getPlayerName]);

  if (loading) return <LoadingSkeleton />;

  if (!match) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,_rgba(10,12,18,1)_0%,_rgba(12,16,23,1)_100%)] px-4 text-white">
        <Card className="max-w-lg rounded-[28px] border-white/10 bg-white/[0.04]">
          <CardContent className="p-8 text-center">
            <Activity className="mx-auto mb-4 h-12 w-12 text-primary" />
            <h2 className="text-2xl font-semibold">The match hasn&apos;t started yet</h2>
            <p className="mt-3 text-sm text-white/55">
              This fixture either hasn&apos;t been created properly or is not available right now. Try another live game or return to the tournament hub.
            </p>
            <Link to="/" className="mt-5 inline-block">
              <Button className="rounded-full">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentInnings = match.innings?.[match.innings.length - 1];
  const firstInnings = match.innings?.[0];
  const secondInnings = match.innings?.[1];
  const runRate = calculateRunRate(currentInnings);
  const isCompleted = match.status === 'completed';
  const currentExtras = getExtrasTotal(currentInnings);
  const needRuns = currentInnings?.target ? Math.max(currentInnings.target - currentInnings.runs, 0) : 0;
  const ballsRemaining = currentInnings?.target ? Math.max(((match.overs - currentInnings.overs) * 6) - currentInnings.balls, 0) : 0;

  const inningsStats = (match.innings || []).map((innings) => {
    const stats = calculatePlayerStats(innings);
    const batsmenArray = Object.entries(stats.batsmen)
      .map(([id, stat]) => ({ id, name: getPlayerName(id), ...stat }))
      .filter((batsman) => batsman.balls > 0)
      .sort((a, b) => b.runs - a.runs);

    const bowlersArray = Object.entries(stats.bowlers)
      .map(([id, stat]) => ({ id, name: getPlayerName(id), ...stat }))
      .filter((bowler) => bowler.overs > 0 || bowler.balls > 0)
      .sort((a, b) => b.wickets - a.wickets || a.runs - b.runs);

    return { innings, batsmenArray, bowlersArray };
  });

  const livePrediction = (() => {
    if (!currentInnings) return null;

    if (currentInnings.target) {
      const wicketsInHand = 10 - currentInnings.wickets;
      const chaseControlRaw = 50 + ((currentInnings.runs / currentInnings.target) * 30) + (wicketsInHand * 2) - (ballsRemaining > 0 ? (needRuns / Math.max(ballsRemaining, 1)) * 8 : 0);
      const winChance = Math.max(5, Math.min(95, Math.round(chaseControlRaw)));
      return {
        headline: `${currentInnings.batting_team?.name} have ${winChance}% live win momentum`,
        subtext: needRuns > 0
          ? `${needRuns} runs needed from ${ballsRemaining} balls with ${wicketsInHand} wickets in hand.`
          : `${currentInnings.batting_team?.name} have reached the target.`,
        homeTeamChance: winChance,
        awayTeamChance: 100 - winChance,
      };
    }

    const projected = Math.round(Number(runRate) * match.overs);
    const inningsControl = Math.max(10, Math.min(95, Math.round((currentInnings.runs / Math.max(projected, 1)) * 100)));
    return {
      headline: `Projected total: ${projected}`,
      subtext: `${currentInnings.batting_team?.name} are scoring at ${runRate} per over.`,
      homeTeamChance: inningsControl,
      awayTeamChance: 100 - inningsControl,
    };
  })();

  const comparisonStats = (() => {
    const team1Name = match.team1?.name || 'Team 1';
    const team2Name = match.team2?.name || 'Team 2';
    const teamStats = {
      [match.team1?.id]: { runs: 0, boundaries: 0, wickets: 0, runRate: 0, inningsCount: 0 },
      [match.team2?.id]: { runs: 0, boundaries: 0, wickets: 0, runRate: 0, inningsCount: 0 },
    };

    (match.innings || []).forEach((innings) => {
      const battingTeamId = innings.batting_team?.id;
      const bowlingTeamId = innings.bowling_team?.id;
      if (battingTeamId && teamStats[battingTeamId]) {
        teamStats[battingTeamId].runs += innings.runs || 0;
        teamStats[battingTeamId].runRate += Number(calculateRunRate(innings));
        teamStats[battingTeamId].inningsCount += 1;
        (innings.ball_by_ball || []).forEach((ball) => {
          if (ball.runs === 4 || ball.runs === 6) teamStats[battingTeamId].boundaries += 1;
          if (ball.is_wicket && bowlingTeamId && teamStats[bowlingTeamId]) teamStats[bowlingTeamId].wickets += 1;
        });
      }
    });

    const team1 = teamStats[match.team1?.id] || { runs: 0, boundaries: 0, wickets: 0, runRate: 0, inningsCount: 0 };
    const team2 = teamStats[match.team2?.id] || { runs: 0, boundaries: 0, wickets: 0, runRate: 0, inningsCount: 0 };

    return {
      team1Name,
      team2Name,
      rows: [
        { label: 'Runs', left: team1.runs, right: team2.runs, accent: 'primary' },
        { label: 'Boundaries', left: team1.boundaries, right: team2.boundaries, accent: 'violet' },
        { label: 'Wickets Taken', left: team1.wickets, right: team2.wickets, accent: 'emerald' },
        {
          label: 'Avg Run Rate',
          left: Number(team1.inningsCount ? (team1.runRate / team1.inningsCount).toFixed(2) : 0),
          right: Number(team2.inningsCount ? (team2.runRate / team2.inningsCount).toFixed(2) : 0),
          accent: 'primary',
        },
      ],
    };
  })();

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(170,255,0,0.08),transparent_20%),linear-gradient(180deg,_rgba(10,12,18,1)_0%,_rgba(12,16,23,1)_100%)] text-white" data-testid="public-scoreboard">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0b1017]/92 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="icon" className="rounded-2xl text-white hover:bg-white/[0.08]">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <BrandLogo compact />
          </div>
          <div className="flex items-center gap-2">
            {match.status === 'live' && (
              websocketEnabled ? (
                wsConnected ? (
                  <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                    <Wifi className="mr-1 h-3 w-3" /> Live
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-full border-red-500/30 bg-red-500/10 text-red-300">
                    <WifiOff className="mr-1 h-3 w-3" /> Reconnecting
                  </Badge>
                )
              ) : (
                <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-primary">
                  <Activity className="mr-1 h-3 w-3" /> Auto refresh
                </Badge>
              )
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-3.5 py-3.5 sm:px-6 sm:py-5">
        <div className="mb-4 text-center sm:mb-5">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {match.status === 'live' && (
              <Badge className="gap-1 rounded-full bg-red-500 text-white pulse-live">
                <Radio className="h-3 w-3" /> LIVE
              </Badge>
            )}
            {match.status === 'completed' && <Badge className="rounded-full bg-emerald-600 text-white">COMPLETED</Badge>}
            {match.status === 'scheduled' && <Badge variant="secondary" className="rounded-full">SCHEDULED</Badge>}
            {match.round_name && <Badge variant="outline" className="rounded-full border-white/10 bg-white/[0.04] text-white/70">{match.round_name}</Badge>}
          </div>
          <h1 className="mt-2 text-[26px] font-bold leading-[1.08] tracking-tight sm:mt-3 sm:text-3xl">
            {match.team1?.name} vs {match.team2?.name}
          </h1>
          <p className="mt-1 text-[13px] text-white/50 sm:text-sm">
            {match.overs} overs match {match.venue && `• ${match.venue}`}
          </p>
          {match.toss && (
            <p className="mt-1.5 text-[12px] text-white/45 sm:mt-2 sm:text-sm">
              {match.toss.won_by?.name} won the toss and elected to {match.toss.elected_to}
            </p>
          )}
        </div>

        {match.result && (
          <Card className="mb-4 rounded-[22px] border-primary/25 bg-primary/8 sm:mb-5 sm:rounded-[28px]">
            <CardContent className="p-4 text-center sm:p-6">
              <Trophy className="mx-auto mb-3 h-12 w-12 text-primary" />
              <p className="text-xl font-bold text-primary">{match.result}</p>
            </CardContent>
          </Card>
        )}

        {match.status === 'scheduled' ? (
          <ContextualState
            icon={Activity}
            eyebrow="Match Status"
            title="The match hasn't started yet"
            description={`Fixture is scheduled for ${new Date(match.match_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}${match.match_time ? ` at ${match.match_time}` : ''}.`}
            tone="dark"
          />
        ) : (
          <>
            {currentInnings && (
              <div className="mb-4 sm:mb-5">
                <Card className="overflow-hidden rounded-[22px] border-primary/20 bg-[linear-gradient(135deg,rgba(170,255,0,0.18),rgba(255,255,255,0.03)_55%,rgba(255,255,255,0.01))] shadow-[0_20px_60px_rgba(0,0,0,0.28)] sm:rounded-[30px]">
                  <div className="border-b border-white/10 bg-black/15 px-4 py-3 sm:px-5 sm:py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        {match.status === 'live' && <div className="h-2.5 w-2.5 rounded-full bg-red-500 pulse-live" />}
                        <span className="text-[14px] font-semibold sm:text-base">{currentInnings.batting_team?.name}</span>
                        {match.innings.length === 2 && <Badge variant="secondary" className="text-xs">2nd Innings</Badge>}
                      </div>
                      <p className="text-[12px] text-white/55 sm:text-sm">
                        {firstInnings && secondInnings
                          ? `${firstInnings.batting_team?.name} ${firstInnings.runs}/${firstInnings.wickets}`
                          : 'Opening innings in progress'}
                      </p>
                    </div>
                  </div>
                  <CardContent className="p-4 sm:p-6">
                    <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr] lg:gap-5">
                      <div>
                        <p className="text-[48px] font-bold tracking-tight text-white score-display sm:text-7xl">
                          {currentInnings.runs}
                          <span className="text-2xl text-white/45 sm:text-4xl">/{currentInnings.wickets}</span>
                        </p>
                        <p className="mt-1 text-base text-white/55 sm:mt-2 sm:text-xl">({currentInnings.overs}.{currentInnings.balls}) overs</p>
                        {currentInnings.target && (
                          <div className="mt-3 rounded-[18px] border border-white/10 bg-black/20 px-3 py-2.5 text-[13px] sm:mt-4 sm:rounded-2xl sm:px-4 sm:py-3 sm:text-sm">
                            Need <span className="font-bold text-primary">{needRuns}</span> runs from{' '}
                            <span className="font-bold text-primary">{ballsRemaining}</span> balls
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:grid-cols-1">
                        <Card className="rounded-[18px] border-white/10 bg-black/20 sm:rounded-2xl">
                          <CardContent className="p-2.5 text-center sm:p-4">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">RR</p>
                            <p className="mt-1 text-lg font-bold text-white score-display sm:mt-2 sm:text-2xl">{runRate}</p>
                          </CardContent>
                        </Card>
                        <Card className="rounded-[18px] border-white/10 bg-black/20 sm:rounded-2xl">
                          <CardContent className="p-2.5 text-center sm:p-4">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">
                              {currentInnings.target ? 'Target' : 'Ext'}
                            </p>
                            <p className="mt-1 text-lg font-bold text-white score-display sm:mt-2 sm:text-2xl">
                              {currentInnings.target || currentExtras}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="rounded-[18px] border-white/10 bg-black/20 sm:rounded-2xl">
                          <CardContent className="p-2.5 text-center sm:p-4">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">Wkts</p>
                            <p className="mt-1 text-lg font-bold text-red-300 score-display sm:mt-2 sm:text-2xl">{currentInnings.wickets}</p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Tabs defaultValue={match.status === 'live' ? 'commentary' : 'scorecard'} className="w-full">
              <TabsList className="mb-4 grid w-full grid-cols-2 rounded-2xl border border-white/10 bg-white/[0.04] p-1 text-white sm:mb-5 sm:grid-cols-4">
                <TabsTrigger value="commentary" className="rounded-xl px-2 py-2 text-[12px] data-[state=active]:bg-primary data-[state=active]:text-black sm:text-sm">
                  Commentary
                </TabsTrigger>
                <TabsTrigger value="scorecard" className="rounded-xl px-2 py-2 text-[12px] data-[state=active]:bg-primary data-[state=active]:text-black sm:text-sm">
                  Scorecard
                </TabsTrigger>
                <TabsTrigger value="headtohead" className="rounded-xl px-2 py-2 text-[12px] data-[state=active]:bg-primary data-[state=active]:text-black sm:text-sm">
                  Head-to-Head
                </TabsTrigger>
                <TabsTrigger value="prediction" className="rounded-xl px-2 py-2 text-[12px] data-[state=active]:bg-primary data-[state=active]:text-black sm:text-sm">
                  Prediction
                </TabsTrigger>
              </TabsList>

              <TabsContent value="commentary" className="space-y-4">
                <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg text-white">
                      <MessageSquareText className="h-5 w-5 text-primary" />
                      Live Commentary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {commentaryItems.length === 0 ? (
                      <ContextualState
                        icon={MessageSquareText}
                        eyebrow="Live Commentary"
                        title="The commentary desk is warming up"
                        description="Ball-by-ball updates will appear here once the innings gets underway."
                        tone="dark"
                        className="rounded-2xl"
                      />
                    ) : (
                      <div className="space-y-3">
                        {commentaryItems.map((item) => (
                          <div key={item.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge
                                className={
                                  item.tone === 'wicket'
                                    ? 'bg-red-500/15 text-red-200'
                                    : item.tone === 'boundary'
                                    ? 'bg-emerald-500/15 text-emerald-200'
                                    : item.tone === 'extra'
                                    ? 'bg-amber-500/15 text-amber-200'
                                    : 'bg-white/10 text-white/75'
                                }
                              >
                                {item.overLabel}
                              </Badge>
                              <span className="text-xs uppercase tracking-[0.24em] text-white/35">{item.inningsLabel}</span>
                            </div>
                            <p className="mt-3 text-base font-semibold text-white">{item.title}</p>
                            <p className="mt-1 text-sm leading-6 text-white/58">{item.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="scorecard" className="space-y-4">
                {inningsStats.map(({ innings, batsmenArray, bowlersArray }, inningsIdx) => (
                  <div key={inningsIdx} className="space-y-4">
                    <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex flex-col gap-3 text-lg text-white sm:flex-row sm:items-center sm:justify-between">
                          <span>{innings.batting_team?.name} - {inningsIdx === 0 ? '1st' : '2nd'} Innings</span>
                          <Badge variant="secondary">
                            {innings.runs}/{innings.wickets} ({innings.overs}.{innings.balls})
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4">
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Total</p>
                            <p className="mt-2 text-2xl font-bold score-display text-white">{innings.runs}</p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Wickets</p>
                            <p className="mt-2 text-2xl font-bold score-display text-red-300">{innings.wickets}</p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Overs</p>
                            <p className="mt-2 text-2xl font-bold score-display text-white">{innings.overs}.{innings.balls}</p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center">
                            <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">
                              {innings.target ? 'Target' : 'Extras'}
                            </p>
                            <p className="mt-2 text-2xl font-bold score-display text-white">
                              {innings.target || getExtrasTotal(innings)}
                            </p>
                          </div>
                        </div>
                        <p className="mt-3 text-xs text-white/40">
                          Extras: W {innings.extras.wides}, NB {innings.extras.no_balls}, B {innings.extras.byes}, LB {innings.extras.leg_byes}
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base text-white">
                          <User className="h-4 w-4 text-primary" />
                          Batting Card
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[620px] text-sm">
                            <thead>
                              <tr className="border-b border-white/10 text-white/45">
                                <th className="px-2 py-2 text-left">Batsman</th>
                                <th className="px-2 py-2 text-center">R</th>
                                <th className="px-2 py-2 text-center">B</th>
                                <th className="px-2 py-2 text-center">4s</th>
                                <th className="px-2 py-2 text-center">6s</th>
                                <th className="px-2 py-2 text-center">SR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batsmenArray.length > 0 ? (
                                batsmenArray.map((batsman) => (
                                  <tr key={batsman.id} className="border-b border-white/5">
                                    <td className="px-2 py-3">
                                      <span className={batsman.isOut ? 'text-white/55' : 'font-medium text-white'}>
                                        {batsman.name}
                                      </span>
                                      {batsman.isOut && (
                                        <span className="ml-1 text-xs text-red-300">({batsman.wicketType})</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-3 text-center font-bold text-white">{batsman.runs}</td>
                                    <td className="px-2 py-3 text-center text-white/70">{batsman.balls}</td>
                                    <td className="px-2 py-3 text-center text-sky-300">{batsman.fours}</td>
                                    <td className="px-2 py-3 text-center text-violet-300">{batsman.sixes}</td>
                                    <td className="px-2 py-3 text-center text-white/70">
                                      {batsman.balls > 0 ? ((batsman.runs / batsman.balls) * 100).toFixed(1) : '0.0'}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="px-2 py-6 text-center text-white/45">
                                    No batting data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base text-white">
                          <Target className="h-4 w-4 text-primary" />
                          Bowling Card
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[520px] text-sm">
                            <thead>
                              <tr className="border-b border-white/10 text-white/45">
                                <th className="px-2 py-2 text-left">Bowler</th>
                                <th className="px-2 py-2 text-center">O</th>
                                <th className="px-2 py-2 text-center">R</th>
                                <th className="px-2 py-2 text-center">W</th>
                                <th className="px-2 py-2 text-center">Eco</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bowlersArray.length > 0 ? (
                                bowlersArray.map((bowler) => {
                                  const totalOvers = bowler.overs + bowler.balls / 6;
                                  const economy = totalOvers > 0 ? (bowler.runs / totalOvers).toFixed(2) : '0.00';
                                  return (
                                    <tr key={bowler.id} className="border-b border-white/5">
                                      <td className="px-2 py-3 font-medium text-white">{bowler.name}</td>
                                      <td className="px-2 py-3 text-center text-white/70">{bowler.overs}.{bowler.balls}</td>
                                      <td className="px-2 py-3 text-center text-white/70">{bowler.runs}</td>
                                      <td className="px-2 py-3 text-center font-bold text-emerald-300">{bowler.wickets}</td>
                                      <td className="px-2 py-3 text-center text-white/70">{economy}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={5} className="px-2 py-6 text-center text-white/45">
                                    No bowling data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    <OverByOverSection innings={innings} inningsIdx={inningsIdx} compact={false} />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="headtohead" className="space-y-4">
                <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg text-white">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      Head-to-Head Snapshot
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {comparisonStats.rows.map((row) => (
                      <ComparisonBar
                        key={row.label}
                        label={row.label}
                        leftLabel={comparisonStats.team1Name}
                        rightLabel={comparisonStats.team2Name}
                        leftValue={row.left}
                        rightValue={row.right}
                        accent={row.accent}
                      />
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="prediction" className="space-y-4">
                <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg text-white">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Match Prediction
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {livePrediction ? (
                      <>
                        <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4">
                          <p className="text-lg font-semibold text-white">{livePrediction.headline}</p>
                          <p className="mt-2 text-sm text-white/60">{livePrediction.subtext}</p>
                        </div>
                        <ComparisonBar
                          label="Live Win Momentum"
                          leftLabel={comparisonStats.team1Name}
                          rightLabel={comparisonStats.team2Name}
                          leftValue={livePrediction.homeTeamChance}
                          rightValue={livePrediction.awayTeamChance}
                          accent="primary"
                        />
                        <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-white/58">
                          This prediction is a live UI estimate based on score pace, wickets in hand, and chase pressure. It updates automatically as the match changes.
                        </div>
                      </>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-white/10 px-4 py-10 text-center text-white/45">
                        Prediction model becomes available once live innings data starts coming in.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicScoreboard;
